import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem, SectionLabel } from "../components/Motion";
import { PRODUCTS, COMING_SOON } from "../lib/site";
import { useLightbox, Watermark } from "../components/Lightbox";

export default function Products() {
  const openLightbox = useLightbox();
  return (
    <>
      <SEO />
      <section data-testid="products-page" className="bg-offwhite pt-40 pb-24 sm:pb-32 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
          <SectionLabel index="HAQILA" title="Product Portfolio" />
          <Reveal className="mt-8 max-w-4xl">
            <h1 data-testid="products-primary-heading" className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
              Coconut Shell Charcoal Briquettes
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-charcoal/65 leading-relaxed">
              Coconut shell charcoal briquettes from Indonesia for wholesale supply and export markets. Explore
              shisha and hookah charcoal or BBQ charcoal for grilling, sourced through selected manufacturing partners.
            </p>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-2 gap-8">
            {PRODUCTS.map((product, i) => (
              <Reveal key={product.slug} delay={i * 0.12}>
                <Link
                  to={product.slug}
                  data-testid={`products-card-${product.short.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group block bg-charcoal text-offwhite overflow-hidden"
                >
                  <div className="relative h-72 sm:h-96 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.imageAlt}
                      loading="lazy"
                      className="w-full h-full object-cover img-spotlight transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/85 via-transparent to-transparent" />
                    <span className="absolute top-5 left-5 text-[10px] font-bold uppercase tracking-[0.25em] bg-offwhite/10 backdrop-blur-md border border-white/15 px-3 py-1.5">
                      Available Now
                    </span>
                    <button
                      type="button"
                      aria-label={`Enlarge ${product.name} photo`}
                      data-testid={`products-card-zoom-${product.short.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openLightbox([{ src: product.image, alt: product.imageAlt, caption: product.name }], 0);
                      }}
                      className="absolute inset-0 cursor-zoom-in"
                    />
                    <Watermark />
                  </div>
                  <div className="p-8 sm:p-10">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{product.name}</h2>
                    <p className="mt-3 text-sm text-offwhite/60 leading-relaxed">{product.description}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-clay group-hover:text-offwhite transition-colors duration-300">
                      View Product
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-24">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">In Development</h2>
            <p className="mt-3 text-sm text-charcoal/60 max-w-xl leading-relaxed">
              Additional commodity categories are being developed through our growing supplier network and are not yet
              available for order.
            </p>
          </Reveal>
          <Stagger className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.07}>
            {[...COMING_SOON, "Other Agricultural Commodities"].map((name) => (
              <StaggerItem key={name}>
                <div
                  data-testid={`products-coming-${name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                  className="bg-offwhite p-6 h-full flex flex-col justify-between gap-10 min-h-[160px]"
                >
                  <span className="self-start text-[10px] font-bold uppercase tracking-[0.25em] text-clay border border-clay/40 px-2.5 py-1">
                    Coming Soon
                  </span>
                  <h3 className="font-display text-base font-bold tracking-tight text-charcoal/70">{name}</h3>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-20 bg-charcoal text-offwhite p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Need a specification sheet?</h2>
              <p className="mt-3 text-sm text-offwhite/60 max-w-lg leading-relaxed">
                Tell us your required product, shape, packaging and destination — we'll match it with a suitable
                manufacturing partner.
              </p>
            </div>
            <Link
              to="/request-a-quote"
              data-testid="products-quote-cta"
              className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300 shrink-0"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
